import classnames from 'classnames';

import { useDisabled, DisabledContext } from '@hh.ru/magritte-common-use-disabled';
import type { PolymorphicComponent } from '@hh.ru/magritte-types';
import { CellItem } from '@hh.ru/magritte-ui-cell/CellItem';
import { CellSkeleton } from '@hh.ru/magritte-ui-cell/Skeleton';
import type { CellProps } from '@hh.ru/magritte-ui-cell/types';
import { isCheckboxComponent, isRadioComponent } from '@hh.ru/magritte-ui-checkbox-radio';
import { enrichWithSkeleton } from '@hh.ru/magritte-ui-skeleton';

import styles from './styles.less';

const isCheckableComponent = (component?: React.ReactElement) => {
    if (!component) {
        return false;
    }
    return isCheckboxComponent(component) || isRadioComponent(component);
};

export const CellComponent: PolymorphicComponent<CellProps, 'div'> = ({
    children,
    vertPadding,
    horPadding,
    withAdditionalRightPadding,
    align = 'center',
    roundedBorder = false,
    onClick,
    onClickLeft,
    left,
    right,
    avatar,
    showDivider = false,
    width,
    disabled,
    'data-qa': dataQa = 'cell',
    Element,
    ...props
}) => {
    let isActive = false;
    const forceDisabled = useDisabled();
    const isComponentDisabled = !!disabled || forceDisabled;
    let ElementType: keyof JSX.IntrinsicElements | React.ComponentType;
    const ElementLeftSide = onClickLeft ? 'button' : 'div';
    const isCheckable = isCheckableComponent(left) || isCheckableComponent(right);
    const additionalProps: Record<string, unknown> = {
        'data-qa': dataQa,
    };

    const additionalPropsLeftSide: Record<string, unknown> =
        ElementLeftSide === 'button'
            ? {
                  type: 'button',
                  onClick: onClickLeft,
              }
            : {};

    if (Element === undefined) {
        ElementType = 'div';
        if (isCheckable) {
            isActive = true;
            ElementType = 'label';
        } else if (onClick) {
            isActive = true;
            ElementType = 'button';
            additionalProps.type = 'button';
            additionalProps.onClick = onClick;
        }
    } else {
        ElementType = Element;
    }

    return (
        <ElementType
            {...props}
            className={classnames(styles.cell, {
                [styles.active]: isActive,
                [styles.vertPadding]: vertPadding,
                [styles.horPadding]: horPadding,
                [styles.withAdditionalRightPadding]: withAdditionalRightPadding,
                [styles.rounded]: roundedBorder,
                [styles.alignCenter]: align === 'center',
                [styles.alignTop]: align === 'top',
                [styles.noSelection]: ElementType === 'button',
                [styles.noRight]: !right,
                [styles.withDivider]: showDivider,
            })}
            disabled={isComponentDisabled}
            {...additionalProps}
            style={{ width }}
        >
            <DisabledContext.Provider value={isComponentDisabled}>
                <ElementLeftSide
                    {...additionalPropsLeftSide}
                    disabled={isComponentDisabled}
                    className={classnames(styles.leftSide, {
                        [styles.activeLeftSide]: ElementLeftSide === 'button',
                    })}
                    data-qa="cell-left-side"
                >
                    {left && <CellItem item={left} />}
                    {avatar && <div className={styles.item}>{avatar}</div>}
                    <div className={styles.content}>{children}</div>
                </ElementLeftSide>
                {right && <CellItem item={right} isRight />}
            </DisabledContext.Provider>
        </ElementType>
    );
};

export const Cell = enrichWithSkeleton(CellComponent, CellSkeleton);
